import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { TreeModule } from 'primeng/tree';
import { SelectModule } from 'primeng/select';
import { TreeNode } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { PermissionNode, RolePermissions } from '../../core/models';

// Read-only Magento-ACL-style viewer: pick a role, see the resource tree with the role's granted
// permissions checked. The role->permission map is config-managed on the backend.
@Component({
    selector: 'app-role-permissions',
    standalone: true,
    imports: [FormsModule, CardModule, TreeModule, SelectModule, TooltipModule],
    template: `
        <p-card header="Role Permissions">
            <div class="flex items-center gap-2 mb-3">
                <span class="font-medium">Role:<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Read-only. Checked items are what the selected role can access." tooltipPosition="top"></i></span>
                <p-select
                    [options]="roleOptions()"
                    [(ngModel)]="selectedRole"
                    (onChange)="applyRole()"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select a role"
                    styleClass="w-56"
                />
            </div>

            @if (error()) {
                <p class="text-red-500 mb-3">{{ error() }}</p>
            }

            <p-tree [value]="nodes()" selectionMode="checkbox" [(selection)]="selectedNodes" [propagateSelectionUp]="false" [propagateSelectionDown]="false" />

        </p-card>
    `
})
export class RolePermissionsPage implements OnInit {
    private readonly api = inject(MiddlewareApi);

    private readonly map = signal<RolePermissions[]>([]);
    readonly nodes = signal<TreeNode[]>([]);
    readonly error = signal<string | null>(null);

    selectedNodes: TreeNode[] = [];
    selectedRole = '';

    readonly roleOptions = computed(() => this.map().map((m) => ({ label: m.role, value: m.role })));

    ngOnInit(): void {
        this.api.getPermissionTree().subscribe({
            next: (t) => {
                this.nodes.set(this.toTreeNodes(t));
                this.applyRole();
            },
            error: (e) => this.error.set(this.describe(e))
        });
        this.api.getRolePermissions().subscribe({
            next: (m) => {
                this.map.set(m);
                if (m.length && !this.selectedRole) {
                    this.selectedRole = m[0].role;
                }
                this.applyRole();
            },
            error: (e) => this.error.set(this.describe(e))
        });
    }

    applyRole(): void {
        const perms = this.map().find((m) => m.role === this.selectedRole)?.permissions ?? [];
        this.selectedNodes = this.collectChecked(this.nodes(), perms);
    }

    private toTreeNodes(nodes: PermissionNode[]): TreeNode[] {
        return nodes.map((n) => ({
            key: n.key,
            label: n.label,
            data: n.permission ?? null,
            selectable: !!n.permission,
            expanded: true,
            children: n.children?.length ? this.toTreeNodes(n.children) : undefined
        }));
    }

    private collectChecked(nodes: TreeNode[], perms: string[]): TreeNode[] {
        const result: TreeNode[] = [];
        const walk = (ns: TreeNode[]) => {
            for (const n of ns) {
                if (n.data && perms.includes(n.data)) result.push(n);
                if (n.children) walk(n.children);
            }
        };
        walk(nodes);
        return result;
    }

    private describe(e: any): string {
        if (e?.status === 403) return 'Forbidden — needs the users:manage permission.';
        if (e?.status === 401) return 'Sign in first.';
        if (e?.status === 0) return 'Cannot reach the API.';
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
